import { Injectable, Signal, resource } from '@angular/core';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  DynamicQueryService,
} from '@services/analytics-services/core';
import { BehaviorSubject, Observable, catchError, of, tap, firstValueFrom } from 'rxjs';

import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import {
  GroupChartTabs,
  GroupedTabData,
} from '@services/analytics-services/core/group-chart.modals';
import {
  GroupedDataState,
  GroupedDataStoreService,
} from '@services/analytics-services/core/grouped-data-store.service';
import { ChartType, QueryType } from '@services/analytics-services/core/models/matrix-stats';
import { OrderStats } from '@services/analytics-services/orders';
import { OrderAnalyticsService } from '@services/analytics-services/orders/order-analytics.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { Conditioner } from '@services/core-services/filters/conditioner';
import { FilterGroupType } from '@services/core-services/filters/filter-group-type';
import { Operator } from '@services/core-services/filters/operator';
import {
  DateRangePresetType,
  PaginatedList,
  Pagination,
  ProcessingState,
  Query,
} from '@services/core-services/models';
import { Orders } from '@services/order-services/models';
import { OrderService } from '@services/order-services/order.service';
import { DashboardStore } from '../dashboard/dashboard.store';

@Injectable()
export class DashboardOrdersStore {
  private orders = new BehaviorSubject<PaginatedList<Orders[]>>({
    list: [],
    pagination: undefined,
  });
  private ordersLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private ordersQuery = new BehaviorSubject<Query>({ page: 1, limit: 5 });

  private associatedCampaignsState: GroupedDataState;

  private errorMessage = new BehaviorSubject<any>('');

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly orderService: OrderService,
    private readonly orderAnalyticsService: OrderAnalyticsService,
    private readonly groupedDataStoreService: GroupedDataStoreService,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService
  ) {
    this.associatedCampaignsState = this.groupedDataStoreService.createGroupedDataState('source');
  }

  getOrderList(): Observable<PaginatedList<Orders[]>> {
    return this.orders.asObservable();
  }

  getOrdersLoader(): Observable<ProcessingState> {
    return this.ordersLoader.asObservable();
  }

  // Associated Campaigns Getters
  getAssociatedCampaignsStats(): Observable<GroupedTabData> {
    return this.associatedCampaignsState.stats.asObservable();
  }
  getAssociatedCampaignsTabs(): Observable<GroupChartTabs[]> {
    return this.associatedCampaignsState.tabs.asObservable();
  }
  getAssociatedCampaignsTabsActiveTab(): Observable<string> {
    return this.associatedCampaignsState.defaultTab.asObservable();
  }
  getAssociatedCampaignsPagination(): Observable<Pagination> {
    return this.associatedCampaignsState.pagination.asObservable();
  }
  getAssociatedCampaignsLoader(): Observable<ProcessingState> {
    return this.associatedCampaignsState.loader.asObservable();
  }

  getErrorMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  public loadDashboardOrders(context: AnalyticsRangeContext) {
    this.ordersLoader.next(ProcessingState.Loading);
    const query = this.ordersQuery.getValue();
    const { range } = context;

    if (!query.filters) {
      query.filters = { condition: Conditioner.AND, filters: [] };
    }

    if (!query.filters.filters) {
      query.filters.filters = [];
    }
    query.filters.filters = query.filters.filters.filter(
      filter => 'field' in filter && !['created_at'].includes(filter.field)
    );

    if (range) {
      query.filters?.filters?.push({
        field: 'created_at',
        value: [range.start, range.end],
        type: FilterGroupType.Data,
        operator: Operator.Between,
      });
      query.filters?.filters?.push({
        field: 'payment_status',
        value: 'success',
        type: FilterGroupType.Data,
        operator: Operator.Equals,
      });
    }

    return this.orderService.getAllOrderData(query).pipe(
      tap(orders => {
        this.ordersLoader.next(ProcessingState.Success);
        this.orders.next(orders);
      }),
      catchError(error => {
        this.ordersLoader.next(ProcessingState.Error);
        const errorMessage = this.errorHandlerService.handleServerError(error);
        this.errorMessage.next(errorMessage);
        return [];
      })
    );
  }

  public loadAssociatedCampaignsData(context: AnalyticsRangeContext): Observable<any> {
    const { range, interval, preset } = context;
    this.associatedCampaignsState.loader.next(ProcessingState.Loading);

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.ASSOCIATED_CAMPAIGNS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap(response => {
          if (!response || response.length === 0) {
            this.associatedCampaignsState.loader.next(ProcessingState.Empty);
            return;
          }
          const data = this.extractResponseList(response);
          this.groupedDataStoreService.processEntityGroupedData(
            this.associatedCampaignsState,
            data,
            'state',
            'associatedCampaigns'
          );
        }),
        catchError(error => {
          console.error(`Error loading associated campaigns data:`, error);
          this.associatedCampaignsState.loader.next(ProcessingState.Error);
          this.handleError('Error loading associated campaigns data', error);
          return of([] as any);
        })
      );
  }

  private extractResponseList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      if (response.list && Array.isArray(response.list)) {
        return response.list;
      } else if (response.stats && Array.isArray(response.stats)) {
        return response.stats;
      } else if (response.content?.list && Array.isArray(response.content.list)) {
        return response.content.list;
      } else if (response.content?.stats && Array.isArray(response.content.stats)) {
        return response.content.stats;
      }
    }
    console.warn('Could not extract list from response:', response);
    return [];
  }

  // --- Pagination and Tab Management ---

  updateAssociatedCampaignsDefaultTab(tabId: string): void {
    this.groupedDataStoreService.updatePagination(this.associatedCampaignsState, tabId, 1);
    this.associatedCampaignsState.defaultTab.next(tabId);
  }

  updateAssociatedCampaignsPagination(tabId: string, page: number, limit: number = 8): void {
    this.groupedDataStoreService.updatePagination(
      this.associatedCampaignsState,
      tabId,
      page,
      limit
    );
  }

  paginateOrders(page: number): Observable<PaginatedList<Orders[]>> {
    const currentQuery = this.ordersQuery.getValue();
    const newQuery = { ...currentQuery, page };
    this.ordersQuery.next(newQuery);

    const context = this.dashboardStore.getRangeContextSnapshot();
    if (context) {
      this.loadDashboardOrders(context).subscribe();
    }

    return this.getOrderList();
  }

  sortOrders(field: string, direction: 'asc' | 'desc'): void {
    const currentQuery = this.ordersQuery.getValue();
    const newQuery = {
      ...currentQuery,
      page: 1,
      sort_by: { [field]: direction },
    };
    this.ordersQuery.next(newQuery);

    const context = this.dashboardStore.getRangeContextSnapshot();
    if (context) {
      this.loadDashboardOrders(context).subscribe();
    }
  }

  resetSorting(): void {
    const currentQuery = this.ordersQuery.getValue();
    const newQuery = {
      ...currentQuery,
      page: 1,
      sort_by: undefined,
    };
    this.ordersQuery.next(newQuery);
    const context = this.dashboardStore.getRangeContextSnapshot();
    if (context) {
      this.loadDashboardOrders(context).subscribe();
    }
  }

  // --- Error Handling ---
  private handleError(message: string, error: any): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    this.errorMessage.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }

  /**
   * Creates a resource for loading order analytics data
   */
  public createOrderAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: ({ params }): Promise<AnalyticsInfo<OrderStats>> => {
        const { range, compare, interval, preset } = params;
        return firstValueFrom(
          this.orderAnalyticsService.getAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
          })
        );
      },
    });
  }
}
